import { useEffect, useRef } from 'react';

import type { TooltipProps } from '@mui/material';
import { Tooltip, tooltipClasses } from '@mui/material';
import { styled } from 'styled-components';

const CustomTooltip = styled(
  ({
    className,
    direction,
    isOpen,
    setIsOpen,
    disableFocusListener = true,
    clickListener = false,
    ...props
  }: TooltipProps & {
    triangleMobilePosition: number;
    trianglePosition: number;
    clickListener?: boolean;
    direction?: 'rtl' | 'ltr';
    isOpen?: boolean;
    setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  }) => {
    const clickProps = clickListener ? { disableHoverListener: true, open: isOpen } : {};

    const TooltipRef = useRef<HTMLDivElement>(null);

    const handleClickOutside = (event: MouseEvent) => {
      if (event?.target && TooltipRef.current && !TooltipRef.current.contains(event.target as Node)) {
        setIsOpen?.(false);
      }
    };

    useEffect(() => {
      if (clickListener) {
        document.addEventListener('click', handleClickOutside);
      }

      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }, [setIsOpen]);

    return (
      <Tooltip
        {...props}
        {...clickProps}
        ref={TooltipRef}
        classes={{ popper: className }}
        dir={direction}
        disableFocusListener={disableFocusListener}
        enterTouchDelay={0}
      />
    );
  },
)(({ trianglePosition, triangleMobilePosition, direction, clickListener }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    '@media only screen and (max-width: 509px)': {
      left: direction === 'rtl' ? 'auto' : '13px',
      maxWidth: '80%',
      right: direction === 'rtl' ? '18px' : 'auto',
    },
    a: {
      color: 'var(--secondary)',
    },
    b: {
      fontWeight: 600,
    },
    background: 'var(--primary)',
    borderRadius: '10px',
    color: 'var(--secondary)',
    display: 'flex',
    flexDirection: 'column',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    maxWidth: '367px',

    opacity: 1,
    padding: '8px 12px',
    rowGap: '8px',
  },
  [`& .${tooltipClasses.arrow}`]: {
    '@media(max-width: 509px)': {
      left:
        direction === 'rtl'
          ? clickListener
            ? '0 !important'
            : '-24px !important'
          : `${triangleMobilePosition}px !important`,
    },
    color: 'var(--primary)',
    height: '12px',
    left: direction === 'rtl' ? '-3px !important' : `${trianglePosition}px !important`,
    opacity: 1,
    top: '98%',
    width: '24px',
  },
}));

export default CustomTooltip;
